import React, { useState } from "react";
import styles from "./Changepassword.module.css";
import { CustomButton, CustomTextInput } from "../../components";
import { AxiosInstance, TokenService } from "../../services/Api";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PopupModal from "../../components/PopupModal/PopupModal";
import useAuth from "../../hooks/useAuth";

const ChangePassword = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const userId = user?.id || 0;

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
  });

  // 🔐 Strong password rules for the NEW password:
  // - At least 1 uppercase letter
  // - At least 1 lowercase letter
  // - At least 1 number
  // - At least 1 special character
  // - Minimum 6 characters
  const validateStrongPassword = (value) => {
    if (!value) return "New password is required";
    if (value.length < 6) return "Password must be at least 6 characters";
    if (!/[A-Z]/.test(value))
      return "Password must contain at least 1 uppercase letter";
    if (!/[a-z]/.test(value))
      return "Password must contain at least 1 lowercase letter";
    if (!/[0-9]/.test(value)) return "Password must contain at least 1 number";
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(value))
      return "Password must contain at least 1 special character";
    return "";
  };

  const validateField = (name, value) => {
    switch (name) {
      case "oldPassword":
        if (!value) return translations.errors.oldPasswordRequired;
        if (value.length < 6) {
          return translations.errors.passworLeastCharacterdRequired;
        }
        return "";

      case "newPassword": {
        const strongError = validateStrongPassword(value);
        if (strongError) return strongError;
        if (formData.oldPassword && value === formData.oldPassword) {
          return translations.errors.newPasswordifferentCharacterdRequired;
        }
        return "";
      }

      case "confirmPassword":
        if (!value) return translations.errors.confirmPassword;
        if (value !== formData.newPassword) {
          return translations.errors.doNotMatchPassowrd;
        }
        return "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (name === "newPassword" && formData.confirmPassword) {
      const confirmError = validateField(
        "confirmPassword",
        formData.confirmPassword,
      );
      setErrors((prev) => ({
        ...prev,
        confirmPassword: confirmError,
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const getFieldError = (fieldName) => {
    return touched[fieldName] ? errors[fieldName] : "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      oldPassword: true,
      newPassword: true,
      confirmPassword: true,
    });

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await AxiosInstance.post(
        `/groupCockpit/changePassword`,
        {
          id: userId,
          old_password: formData.oldPassword,
          new_password: formData.newPassword,
        },
      );

      if (response.data?.success) {
        setFormData({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setTouched({});
        setErrors({});
        setModalState({
          open: true,
          type: "success",
          message: response.data?.message || "Password changed successfully!",
        });
      } else {
        setErrors((prev) => ({
          ...prev,
          oldPassword: response.data?.message || "Failed to change password",
        }));
      }
    } catch (error) {
      console.error("Error changing password:", error);
      setErrors((prev) => ({
        ...prev,
        oldPassword:
          error.response?.data?.message ||
          "An error occurred. Please try again.",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    formData.oldPassword &&
    formData.newPassword &&
    formData.confirmPassword &&
    !errors.oldPassword &&
    !errors.newPassword &&
    !errors.confirmPassword;

  const handleSuccessOk = async () => {
    setModalState((prev) => ({ ...prev, open: false }));

    if (modalState.type === "success") {
      try {
        await logout();
      } catch (err) {
        console.error("Logout failed:", err);
      }
      navigate("/login", { replace: true });
    }
  };

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      <div className={styles.changePasswordCard}>
        <h2 className={styles.cardTitle}>
          {translations.labels.changePassword}
        </h2>

        <form onSubmit={handleSubmit} className={styles.form} noValidate>
          <div>
            <CustomTextInput
              type="password"
              name="oldPassword"
              label={translations.labels.oldPassword}
              placeholder={translations.labels.oldPasswordPlaceholder}
              value={formData.oldPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={getFieldError("oldPassword")}
              maxLength={50}
              autoComplete="current-password"
              required
              disabled={isSubmitting}
            />
          </div>

          <div>
            <CustomTextInput
              type="password"
              name="newPassword"
              label={translations.labels.newPassword}
              placeholder={translations.labels.newPasswordPlaceholder}
              value={formData.newPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={getFieldError("newPassword")}
              maxLength={50}
              autoComplete="new-password"
              required
              disabled={isSubmitting}
            />
          </div>

          <div>
            <CustomTextInput
              type="password"
              name="confirmPassword"
              label={translations.labels.retypeNewPassword}
              placeholder={translations.labels.confirmPasswordPlaceholder}
              value={formData.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={getFieldError("confirmPassword")}
              maxLength={50}
              autoComplete="new-password"
              required
              disabled={isSubmitting}
            />
          </div>

          <div>
            <CustomButton
              type="submit"
              variant="primary"
              className={styles.updateButton}
              disabled={!isFormValid || isSubmitting}
              loading={isSubmitting}
            >
              {isSubmitting ? "SAVING..." : translations.labels.save}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default ChangePassword;
